import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import PdfRender from "../../../../components/pdfRender/pdfRender";
import { RootState } from "../../../../redux/store/store";
import { logDataToConsole } from "../../../../utils/customLogger";
import { getGeneralFormConfig, getOncologyFormConfig } from "../../formConfigs";
import { FaxFormData, FormFieldConfig } from "../../types";
import FormSection from "../formSection/formSection";

interface FaxFormFieldsProps {
  formType: "oncology" | "general";
  formData: FaxFormData;
  onFormDataChange: (data: FaxFormData) => void;
  onValidationChange: (isValid: boolean) => void;
  orderId: string;
}

const FaxFormFields = ({
  formType,
  formData,
  onFormDataChange,
  onValidationChange,
  orderId,
}: FaxFormFieldsProps) => {
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const [formConfig, setFormConfig] = useState<FormFieldConfig[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [documentUrls, setDocumentUrls] = useState<string[] | null>(null);

  useEffect(() => {
    const config =
      formType === "oncology"
        ? getOncologyFormConfig()
        : getGeneralFormConfig();
    setFormConfig(config);
  }, [formType]);

  // Fetch document URLs like in procedure questionnaire
  const fetchDownloadUrl = async (refUrl: string) => {
    try {
      const downloadUrl = await getFileDownloadUrl(refUrl);
      setDocumentUrls((prev) => [...(prev || []), downloadUrl]);
    } catch (err) {
      logDataToConsole("Error fetching download url", err);
    }
  };

  useEffect(() => {
    if (singleMedicalPaOrder?.clinical_document?.file_path) {
      fetchDownloadUrl(singleMedicalPaOrder.clinical_document.file_path);
    }
  }, [singleMedicalPaOrder]);

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    let isValid = true;

    formConfig.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.required && !formData[field.name as keyof FaxFormData]) {
          errors[field.name] = `${field.label} is required`;
          isValid = false;
        }
      });
    });

    setValidationErrors(errors);
    onValidationChange(isValid);
  }, [formConfig, formData, onValidationChange]);

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const updatedData = {
      ...formData,
      [fieldName]: value,
    };
    onFormDataChange(updatedData);
  };

  return (
    <div className="fax-form-fields flex h-full w-full overflow-hidden rounded-t-lg border bg-[#F7F9FA]">
      <div className="fax-form-fields__documents ml-4 mr-4 mt-4 flex-1 overflow-auto">
        {documentUrls && documentUrls.length > 0 ? (
          documentUrls.map((urlItem, index) => (
            <PdfRender key={index} file={urlItem} />
          ))
        ) : (
          <LoaderMessage message="Fetching documents..." />
        )}
      </div>

      <div className="fax-form-fields__form flex flex-1 flex-col gap-4 overflow-auto border-l bg-white p-4">
        <div className="mb-2">
          <h3 className="mb-2 text-lg font-semibold text-primaryGray-1">
            {formType === "oncology"
              ? "Medicare Part B Oncology"
              : "Medicare Medical (Part B)"}{" "}
            Prior Authorization Form
          </h3>
          <p className="text-sm text-primaryGray-10">
            Fill out all required fields marked with an asterisk (*). Form data
            will be used to generate the PDF for submission.
          </p>
        </div>

        <div className="form-sections space-y-6">
          {formConfig.map((section, index) => (
            <FormSection
              key={section.title}
              section={section}
              formData={formData}
              validationErrors={validationErrors}
              onFieldChange={handleFieldChange}
              isLast={index === formConfig.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FaxFormFields;
